import React from 'react';
import { Autocomplete, Button, Card, CardContent, Fab, FormGroup, Grid, IconButton, Modal, Stack, TextField, Tooltip } from '@mui/material';
import config from 'src/config';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import showLoading from 'src/common/loading';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import LabelIcon from '@mui/icons-material/Label';
import SyncIcon from '@mui/icons-material/Sync';
import constants from '../constants/common';
import { addContainer, getContainers } from 'src/services/container.service';
import { suggestLabels } from 'src/services/label.service';
import { generateLabels, getFile, updateFile } from 'src/services/file.service';
import ContainersAutocomplete from '../autocomplete/containers';
import FileAuditLogModal from '../file-audit-log/FileAuditLog.modal';

// ----------------------------------------------------------------------

const flags = constants.flags;


class BoardInfoModal extends React.Component {

	constructor(props) {
		super(props);

		this.state = {

			role: localStorage.getItem('role') || config.USER_ROLES.USER,
			mode: this.props.file ? 'edit' : 'add',
			add_view_type: 'fab',
			is_modal_open: this.props.open || false,

			flags: [],
			labels: [],
			label_suggestions: [],

			containers: [],
			container_suggestions: [],

			generating_labels: false,

			file: null,
			is_owner: false,
			name: ''
		}
	}

	// promised setState
	setStateAsync(state) {
		return new Promise((resolve) => {
			this.setState(state, resolve)
		});
	}

	async componentDidMount() {
		if (this.props.file) {
			this.fetchFile();
		}
	}

	async componentDidUpdate(prevProps) {
		if (this.props.file && this.props.file !== prevProps.file) {
			this.fetchFile();
		}
	}

	async fetchFile() {

		let file = await getFile(this.props.file._id);
		if (!file) {
			return;
		}
		if (file.error) {
			Swal.fire({
				icon: 'error',
				title: 'Error',
				text: file.error,
				confirmButtonText: 'Ok',
				confirmButtonColor: config.primaryColor
			});
			return;
		}

		let logged_in_username = localStorage.getItem('username');
		file = file.file;
		await this.setState({
			file: file,
			is_owner: file?.owner?.username === logged_in_username,
			name: file?.name ?? '',
			flags: file?.flags ? file.flags : [],
			labels: file?.labels ? file.labels : [],
			containers: file?.containers ? file.containers.map(container => {
				return {
					id: container._id,
					label: container.name,
					type: container.type,
					color: container.color,
					is_owner: container.is_owner
				}
			}) : [],
			mode: 'edit',
			is_modal_open: true
		});

	}
		

	isFormValid() {
		if (!this.state.name || this.state.name.trim() === '') {
			Swal.fire({
				icon: 'error',
				title: 'Error',
				text: 'Name is required'
			});
			return false;
		}
		return true;
	}

	clearForm() {
		this.setState({
			file: null,
			name: '',
			flags: [],
			labels: [],
			containers: [],
			label_suggestions: [],
			container_suggestions: []
		});
	}

	async editNote() {
		if (!this.isFormValid())
			return;

		showLoading(true);
		let editNoteRes = await updateFile(this.props.file._id, {
			name: this.state.name,
			flags: this.state.flags,
			labels: this.state.labels,
			containers: this.state.containers.map(container => container.id),
			organized: true
		});
		if (editNoteRes.error) {
			Swal.fire({
				icon: 'error',
				title: 'Error',
				text: editNoteRes.error
			});
		} else {
			Swal.fire({
				icon: 'success',
				title: 'Success',
				text: 'Note organized successfully'
			});
			this.setState({
				is_modal_open: false,
			});
			this.clearForm();
			if (this.props.onClose)
				this.props.onClose();
		}
	}

	async organizeNote() {
		await this.editNote();

		if (this?.props?.onReload) {
			await this.props.onReload();
		}
	}

	generateLabels = async () => {
		try {

			await this.setStateAsync({
				generating_labels: true
			});

			let labels_response = await generateLabels(this.props.file._id);
			if (labels_response.error) {
				console.log("Error generating labels:", labels_response.error);
				alert("Error generating labels - " + labels_response.error)
				await this.setStateAsync({
					generating_labels: false
				});
				return;
			}

			let generated_labels = labels_response.labels;
			let labels = this.state.labels;
			labels = labels.concat(generated_labels);

			// unique labels
			labels = [...new Set(labels)];

			await this.setStateAsync({
				generating_labels: false,
				labels: labels
			});
		} catch (error) {
			await this.setStateAsync({
				generating_labels: false
			});
			alert("Unknown error occurred while generating labels - " + error.message)
			console.log("Unknown error occurred while generating labels - ", error.message)
		}
	}

	updateLabelSuggestions = async (e) => {
	
		let query = e.target.value;

		let label_suggestions = await suggestLabels({ query });

		this.setState({
			label_suggestions: label_suggestions?.next_labels ? label_suggestions.next_labels : []
		});
	}

	updateContainerSuggestions = async (params=null) => {
		try {

			let query = {
				limit: 10,
				skip: 0
			};

			let search = '';
			if (params) {
				search = params.target.value;
				query.search = search;
			}

			let containers = await getContainers(query);
			
			if (containers.error) {
				console.log("Error fetching containers:", containers.error);
				this.setState({
					container_suggestions: []
				});
				return;
			}

			containers = containers.containers;
			
			let container_suggestions = containers.map(container => {
				return {
					id: container._id,
					label: container.name,
					type: container.type,
					color: container.color,
					is_owner: container.is_owner
				}
			});

			if (search && search.trim() !== '') {
				container_suggestions.push({
					id: search,
					label: `Create "${search}"`,
					is_owner: true,
					create: true
				});
			}
			

			this.setState({
				container_suggestions: container_suggestions
			});

		} catch (error) {
			console.error("Error fetching container suggestions:", error);
		}
	}

	createContainer = async (container_name) => {
		let createdContainer = await addContainer({
			name: container_name
		})
		if (createdContainer.error) {
			return null;
		}
		return createdContainer.container;
	}

	render() {
		return (
			<>
			{(!this.props.hideAddButton) ? (
				<Fab size="small" color="secondary" aria-label="add"
					onClick={() => {
						this.setState({
							is_modal_open: true,
							mode: 'add',
							file: null,
							name: '',
						});
					}}
				>
					<StickyNote2Icon />
				</Fab>
			) : null}
			<Modal
				open={this.state.is_modal_open}
				onClose={() => {
					this.setState({
						is_modal_open: false,
						file: null,
						name: '',
					});
					if (this.props.onClose)
						this.props.onClose();
				}}
			>
				<Grid container>
					<Grid item style={modalStyle} xs={12} md={8} lg={6}>
						<Card variant='outlined'>
							<CardContent style={{ padding: 20 }}>
								<Grid container spacing={2}>
									
									<Grid item xs={12}>
										<Stack spacing={1} direction={'row'}>
											<TextField
												label={'Name'}
												variant="outlined"
												value={this.state.name}
												onChange={(e) => {
													this.setState({
														name: e.target.value
													});
												}}
												sx={{ flex: 1 }}
												size="small"
											/>
	
											<Grid display={'flex'} flexDirection={'row'} alignItems="center">
											<FormGroup sx={{ flexDirection: 'row', alignItems: 'center' }} >
												{flags.map((flag, index) => {
													return (
														<Tooltip key={index} title={flag.label} arrow>
															<IconButton
																sx={{
																	border: this.state.flags.includes(flag.value) ? `1px solid ${flag.color}` : '',
																	marginRight: '5px',
																}}
																onClick={() => {
																	if (this.state.flags.includes(flag.value)) {
																		this.setState({
																			flags: this.state.flags.filter(f => f !== flag.value)
																		});
																	} else {
																		this.setState({
																			flags: [...this.state.flags, flag.value]
																		});
																	}
																}}
															>
																{this.state.flags.includes(flag.value) ? flag.iconSelected : flag.icon}
															</IconButton>
														</Tooltip>
													)
												})}
											</FormGroup>
											</Grid>
	
										</Stack>
									</Grid>

									<Grid item xs={12}>
										<Stack spacing={1} direction={'row'}>
											<Autocomplete
												freeSolo
												multiple
												id="labels-outlined"
												options={this.state.label_suggestions}
												value={this.state.labels}
												onChange={(event, newValue) => {
													this.setState({
														labels: newValue
													});
												}}
												renderInput={(params) => (
													<TextField
														{...params}
														variant="outlined"
														label="Labels"
														placeholder="What is it ?"
														InputProps={{
															...params.InputProps,
															endAdornment: (
																<React.Fragment>
																	{params.InputProps.endAdornment}
																	<LabelIcon />
																</React.Fragment>
															),
															enterKeyHint: 'enter'
														}}
														onChange={this.updateLabelSuggestions}
													/>
												)}
												sx={{ width: '100%' }}
												size="small"
												enterKeyHint='enter'
											/>
											<Button variant="outlined" size="small"
												sx={{textTransform: 'none', width: '100%', maxWidth: 'fit-content'}}
												disabled={this.state.generating_labels}
												onClick={this.generateLabels}
												startIcon={
													(this.state.generating_labels) ? (
														<SyncIcon style={{color: 'gray', animation: 'spin 2s linear infinite reverse'}} />
													) : null
												}
											>
												{(this.state.generating_labels) ? ' Generating labels...' : 'Generate labels'}
											</Button>
										</Stack>
									</Grid>

									<Grid item xs={12}>
										<Stack spacing={1} direction={'row'}>
											<ContainersAutocomplete
												disabled={!this.state.is_owner}
												containers={this.state.containers}
												onChange={(updatedContainers) => {
													this.setState({
														containers: updatedContainers
													});
												}}
											/>
										</Stack>
									</Grid>

									<Grid item xs={12}>
										<Stack spacing={1} direction={'row'}>
											<Button
												disableElevation
												fullWidth
												variant="contained"
												onClick={async () => {
													this.organizeNote();
												}}
												disabled={this.state.loading}
												size="small"
											>
												{this.state.loading ? 'Organizing...' : 'Organize'}
											</Button>
											<FileAuditLogModal
												view_type='icon'
												file={this.state.file}
											/>
										</Stack>
									</Grid>

								</Grid>

							</CardContent>
						</Card>
					</Grid>
				</Grid>
			</Modal>
			</>
		);
	}
}

const modalStyle = {
	position: 'absolute',
	top: '50%',
	left: '50%',
	transform: 'translate(-50%, -50%)',
	width: '100%',
	bgcolor: 'background.paper',
	boxShadow: 24,
	p: 2,
	maxHeight: '90%',
	overflowY: 'auto'
};

export default function (props) {
	const navigate = useNavigate();

	return <BoardInfoModal {...props} navigate={navigate} />;
}
